import type { PaymentRoute } from '../api/portal'
import { payLinkLabel } from '../payLinkLabel'

/**
 * How a private lesson is paid, in one sentence (D-691, D-705).
 *
 * <b>One set of words, wherever the question is answered.</b> The family's
 * schedule said this first; the confirmation she answers before agreeing to a
 * lesson a teacher arranged says it too (D-705). Written twice, the two would
 * drift - and these are the sentences that decide whether a family goes and pays
 * somebody, which is the worst place for two screens to disagree.
 *
 * <b>The server picks the route and this never does.</b> `PrivatePayment` is the
 * branch; a family holding credit is not told to pay the teacher even when the
 * teacher has a link, because the route already said so before it arrived here.
 *
 * <b>Nothing for an unknown route or a link route with no link</b>, rather than a
 * guess. The link route is only ever sent with its link; if that ever breaks, a
 * blank line is the honest failure and a made-up sentence is not.
 */
export default function PaymentLine({
  route,
  links,
  className,
}: {
  route: PaymentRoute | null | undefined
  /** Every address she gave, in her order (D-725). */
  links: string[] | null | undefined
  className?: string
}) {
  if (route === 'FromCredit') {
    return (
      <div className={className}>
        Nothing to pay &mdash; the studio takes this one out of your credit.
      </div>
    )
  }

  if (route === 'ToTeacherUnknownHow') {
    return (
      <div className={className}>
        Paid directly to your teacher &mdash; ask her how she takes payment.
      </div>
    )
  }

  if (route === 'ToTeacherAtLink' && links && links.length > 0) {
    return (
      <div className={className}>
        Paid directly to your teacher &mdash;{' '}
        {/*
          <b>Big enough to tap</b>, which the sentence around it does not have
          to be. This is the one thing a parent actually reaches for on a phone,
          and at 11.5px with no padding the target was about 15px - under the
          24px `audit.mjs` measures.

          <b>The padding is what does it</b>, not `min-h`: an arbitrary text size
          sets no leading, so preflight's 1.5 gives a ~17px line box and
          `py-[5px]` carries it to ~27px. `min-h-[24px]` never binds and is kept
          only as a floor if the type ever shrinks.
        */}
        {links.map((link, i) => (
          <span key={link}>
            {i > 0 && <span aria-hidden="true"> &middot; </span>}
            <a
              href={link}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-block min-h-[24px] py-[5px] wrap-anywhere font-semibold text-accent underline underline-offset-2"
            >
              {payLinkLabel(link)}
            </a>
          </span>
        ))}
      </div>
    )
  }

  return null
}
